import {HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AnyAdapter} from '@drl/core/api/adapters/any-adapter.service';
import {CountAdapter} from '@drl/core/api/adapters/count-adapter.service';
import {ApiService} from '@drl/core/api/api.service';
import {CurrentUserAdapter} from '@drl/core/auth/adapters/current-user-adapter.service';
import {User} from '@drl/core/auth/models/user.model';
import {Count} from '@drl/core/models';
import {SystemStoreFacadeService} from '@drl/core/store/system-store-facade.service';
import {forkJoin, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';

import {AddUserCommand} from '../commands/add-user.command';
import {DelUserCommand} from '../commands/del-user.command';
import {GetUsersCountCommand} from '../commands/get-users-count.command';
import {GetUsersCommand} from '../commands/get-users.command';
import {UpdateUserCommand} from '../commands/update-user.command';
import {UserPatchAdapter} from '../adapters/user-patch-adapter.service';
import {GetPatientUsersCountByCancerTypes, GetUserCommand} from '../commands';
import {AddUserImageCommand} from '../commands/add-user-image.command';
import {UserStatus} from '@drl/core/enums';
const baseLimit = 10;
@Injectable()
export class UserFacadeService {
  constructor(
    private readonly apiService: ApiService,
    private readonly anyAdapter: AnyAdapter,
    private readonly countAdapter: CountAdapter,
    private readonly userAdapter: CurrentUserAdapter,
    private readonly userPatchAdapter: UserPatchAdapter,
    private readonly systemStoreFacade: SystemStoreFacadeService,
  ) {}

  getUsers(
    limit = baseLimit,
    offset = 0,
    sortField?: string,
    sortOrder?: number,
    ...rest
  ): Observable<User[]> {
    const command: GetUsersCommand<User> = new GetUsersCommand(
      this.apiService,
      this.userAdapter,
    );

    let order = ['createdOn DESC'];

    const [name, email, phone, rolesPermitted, status, ids] = rest;
    const queryData = {
      roleId: undefined,
      and: undefined,
      or: undefined,
      category: undefined,
      email: undefined,
      phone: undefined,
      status: undefined,
      id: undefined,
    };
    if (sortField && sortOrder === 1) {
      order = [`${sortField} DESC`];
    } else if (sortField && sortOrder === -1) {
      order = [`${sortField} ASC`];
    } else {
      // Do nothing
    }

    if (name) {
      queryData.or = [
        {
          firstName: {
            ilike: `${name}%`,
          },
        },
        {
          middleName: {
            ilike: `${name}%`,
          },
        },
        {
          lastName: {
            ilike: `${name}%`,
          },
        },
      ];
    }

    if (email) {
      queryData.email = {
        ilike: `${email}%`,
      };
    }
    if (phone) {
      queryData.phone = {
        ilike: `${phone}%`,
      };
    }

    if (rolesPermitted && rolesPermitted.length > 0) {
      queryData.roleId = {
        inq: rolesPermitted,
      };
    }

    // if (queryData.roleId && queryData.or) {
    //   queryData.and = [
    //     {
    //       roleId: {
    //         inq: rolesPermitted,
    //       },
    //     },
    //     {
    //       or: queryData.or,
    //     },
    //   ];

    //   delete queryData.roleId;
    //   delete queryData.or;
    // }

    if (queryData.roleId && queryData.or) {
      queryData.and = [
        {
          or: queryData.or,
        },
      ];
      delete queryData.or;
    }

    if (status && status.length > 0) {
      queryData.status = {
        inq: status,
      };
    }

    if (ids) {
      queryData.id = {
        inq: ids,
      };
    }

    const queryStr = this._validateLimit(limit, queryData, order, offset);

    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };
    return forkJoin([
      this.systemStoreFacade.getRoles(),
      command.execute(),
    ]).pipe(
      map(data => {
        data[1].forEach(user => {
          user.role = data[0].find(role => role.id === user.roleId);
        });
        return data[1];
      }),
    );
  }

  getCgPatientsCountByCancerType(id) {
    const command: GetPatientUsersCountByCancerTypes<{
      [key: string]: Count;
    }> = new GetPatientUsersCountByCancerTypes(
      this.apiService,
      this.anyAdapter,
      id,
    );
    return command.execute();
  }

  getUsersCount(
    sortField?: string,
    sortOrder?: number,
    createdOn?: {startDate: Date; endDate: Date},
    ...rest
  ): Observable<Count> {
    const command: GetUsersCountCommand<Count> = new GetUsersCountCommand(
      this.apiService,
      this.countAdapter,
    );
    const [
      name,
      mName,
      lName,
      email,
      phone,
      rolesPermitted,
      comorbidity,
      gender,
      status,
    ] = rest;
    const queryData = {
      firstName: undefined,
      category: undefined,
      roleId: undefined,
      middleName: undefined,
      lastName: undefined,
      email: undefined,
      phone: undefined,
      comorbidity: undefined,
      gender: undefined,
      status: undefined,
      createdOn: undefined,
    };
    if (name) {
      queryData.firstName = {
        ilike: `${name}%`,
      };
    }
    if (mName) {
      queryData.middleName = {
        ilike: `${mName}%`,
      };
    }
    if (lName) {
      queryData.lastName = {
        ilike: `${lName}%`,
      };
    }
    if (email) {
      queryData.email = {
        ilike: `${email}%`,
      };
    }
    if (phone) {
      queryData.phone = {
        ilike: `${phone}%`,
      };
    }
    if (rolesPermitted && rolesPermitted.length > 0) {
      queryData.roleId = {
        inq: rolesPermitted,
      };
    }
    if (comorbidity) {
      queryData.comorbidity = comorbidity;
    }
    if (gender) {
      queryData.gender = gender;
    }
    if (status) {
      queryData.status = status;
    }
    if (createdOn && createdOn.startDate && createdOn.endDate) {
      queryData.createdOn = {
        between: [createdOn.startDate, createdOn.endDate],
      };
    }

    const queryStr = JSON.stringify(queryData);
    command.parameters = {
      query: new HttpParams().set('where', queryStr),
    };
    return command.execute();
  }

  removeUser(userId: number): Observable<void> {
    const command: DelUserCommand<void> = new DelUserCommand(
      this.apiService,
      this.anyAdapter,
      userId,
    );
    return command.execute();
  }

  addUser(user): Observable<User> {
    const command: AddUserCommand<User> = new AddUserCommand(
      this.apiService,
      this.userAdapter,
    );
    command.parameters = {
      data: user,
    };
    return command.execute();
  }

  updateUser(user): Observable<void> {
    if (!user.middleName) {
      user.middleName = '';
    }
    if (!user.lastLogin) {
      delete user.lastLogin;
    }
    const command: UpdateUserCommand<void> = new UpdateUserCommand(
      this.apiService,
      this.userPatchAdapter,
      user.id,
    );

    command.parameters = {
      data: user,
    };
    return command.execute();
  }

  getUserById(userId: number) {
    const command: GetUserCommand<User> = new GetUserCommand(
      this.apiService,
      this.userAdapter,
      userId,
    );
    return command.execute();
  }

  uploadUserImage(userImage, id: number) {
    if (!userImage) {
      return of('No Image was passed');
    }
    const command: AddUserImageCommand<FormData> = new AddUserImageCommand(
      this.apiService,
      this.anyAdapter,
      id,
    );
    command.parameters = {
      data: userImage,
    };
    return command.execute();
  }

  private _validateLimit(
    // this function is just used as a helper function as because above getUsers() function complexity increased 15 to 17.
    limit: number,
    queryData: Object,
    order: string[],
    offset: number,
  ) {
    let queryStr: string;
    if (limit >= 1) {
      queryStr = JSON.stringify({
        where: queryData,
        order,
        limit,
        offset,
      });
    } else {
      queryStr = JSON.stringify({
        where: queryData,
        order,
        offset,
      });
    }
    return queryStr;
  }

  getPatient(patientRoleId: number[], phone?: string, extId?: string) {
    const notAllowedStatus = [
      UserStatus.DEACTIVATED,
      UserStatus.INVITATION_SMS_SENT,
    ];

    const command: GetUsersCommand<User> = new GetUsersCommand(
      this.apiService,
      this.userAdapter,
    );
    if (phone)
      command.parameters = {
        query: new HttpParams().set(
          'filter',
          JSON.stringify({
            where: {
              phone: {
                ilike: '%' + phone.replace('+', ''),
              },
              roleId: {
                inq: patientRoleId,
              },
              status: {
                nin: notAllowedStatus,
              },
            },
          }),
        ),
      };
    else
      command.parameters = {
        query: new HttpParams().set(
          'filter',
          JSON.stringify({
            where: {
              externalId: extId,
              roleId: {
                inq: patientRoleId,
              },
              status: {
                nin: notAllowedStatus,
              },
            },
          }),
        ),
      };
    return command.execute();
  }
}
